import { ClassroomType } from '../../peekapak-types/DataProtocolTypes';
import { CompletionStatus, ELAStandardsMap } from '../../peekapak-types/LessonPlanTypes';
import { getMiddleSchoolUnits, getHighSchoolUnits, MiddleSchoolCard } from '../components/LibraryCard/cardData';

export const getSecondaryCompletion = (completionStatus: ClassroomType['completionStatus'], grade: number) => {
  const middleSchoolUnitIDs = getMiddleSchoolUnits().map((u) => u.unit);
  const highSchoolUnitIDs = getHighSchoolUnits().map((u) => u.unit);

  return Object.keys(completionStatus)
    .filter((key) => (grade > 8 ? highSchoolUnitIDs.includes(key) : middleSchoolUnitIDs.includes(key)))
    .reduce<CompletionStatus>((obj, key) => {
      (obj[key as keyof CompletionStatus] as unknown) = completionStatus[key];
      return obj;
    }, {});
};

export const getNumModules = (grade: number) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  return (grade > 8 ? highSchoolUnits : middleSchoolUnits).reduce((acc, curr) => (acc += curr.numModules), 0);
};

export const getNumCompleteModules = (secondaryCompletion: CompletionStatus) => {
  return Object.values(secondaryCompletion).reduce((acc, curr) => {
    for (const value of Object.values(curr)) {
      for (const nestedValue of Object.values(value)) {
        if (nestedValue === 'Completed' || nestedValue === 'DeemedCompleted') acc += 1;
      }
    }
    return acc;
  }, 0);
};

export const getUnitCompletion = (secondaryCompletion: CompletionStatus, grade: number) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];

  return (grade > 8 ? highSchoolUnits : middleSchoolUnits).map((unit) => {
    return {
      id: unit.unit,
      displayName: unit.displayName,
      totalModules: unit.numModules,
      completeModules: secondaryCompletion[unit.unit]
        ? Object.values(secondaryCompletion[unit.unit]).reduce((acc, curr) => {
            for (const value of Object.values(curr)) {
              if (value === 'Completed' || value === 'DeemedCompleted') acc += 1;
            }
            return acc;
          }, 0)
        : 0,
    };
  });
};

export const getAllApplicableStandards = (standardsMap: ELAStandardsMap) => {
  return Object.values(standardsMap.data).reduce((acc, curr) => {
    for (const value of Object.values(curr)) {
      acc.push(...value.standards);
    }
    return acc;
  }, [] as string[]);
};

export interface LessonWithStandards {
  unitId: string;
  unitNum: number;
  displayName: string;
  lessonId: string;
  standards: string[];
}
export type LessonsWithStandards = LessonWithStandards[];

export const getCompletedLessonsWithStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const secondaryUnits = grade > 8 ? highSchoolUnits : middleSchoolUnits;

  return Object.entries(secondaryCompletion).reduce((acc, curr) => {
    for (const [lessonId, value] of Object.entries(curr[1])) {
      if (
        Object.values(value).every((v) => v === 'Completed' || v === 'DeemedCompleted') &&
        Object.values(value).length === standardsMap?.data[curr[0]][lessonId].numberOfModules
      ) {
        acc.push({
          unitId: curr[0],
          unitNum: secondaryUnits.map((unit) => unit.unit).indexOf(curr[0]),
          lessonId: lessonId,
          displayName: secondaryUnits.find((unit) => unit.unit === curr[0])?.displayName || curr[0],
          standards: standardsMap.data[curr[0]][lessonId].standards,
        });
      }
    }
    return acc;
  }, [] as LessonsWithStandards);
};

export const getIncompleteLessonsWithStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const secondaryUnits = grade > 8 ? highSchoolUnits : middleSchoolUnits;

  return Object.entries(standardsMap.data).reduce((acc, curr) => {
    for (const [lessonId, value] of Object.entries(curr[1])) {
      if (
        !secondaryCompletion[curr[0]] ||
        !secondaryCompletion[curr[0]][lessonId] ||
        Object.values(secondaryCompletion[curr[0]][lessonId]).length !== value.numberOfModules ||
        Object.values(secondaryCompletion[curr[0]][lessonId]).every((v) => v !== 'Completed' && v !== 'DeemedCompleted')
      ) {
        acc.push({
          unitId: curr[0],
          unitNum: secondaryUnits.map((unit) => unit.unit).indexOf(curr[0]),
          lessonId: lessonId,
          displayName: secondaryUnits.find((unit) => unit.unit === curr[0])?.displayName || curr[0],
          standards: standardsMap.data[curr[0]][lessonId].standards,
        });
      }
    }
    return acc;
  }, [] as LessonsWithStandards);
};

export const getUniqueMetStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
): string[] => {
  const standardsOnly = getCompletedLessonsWithStandards(secondaryCompletion, standardsMap, grade).map(
    (lesson) => lesson.standards,
  );
  return [...new Set(standardsOnly.flat())];
};

export const getTotalCompletedStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
) => getUniqueMetStandards(secondaryCompletion, standardsMap, grade).length;

export const getCompleteAndIncompleteByUnit = (
  completedLessons: LessonsWithStandards,
  incompleteLessons: LessonsWithStandards,
  grade: number,
): {
  id: string;
  displayName: string;
  completeStandards: string[];
  incompleteStandards: string[];
}[] => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];

  const unitStandardsCompletion = (grade > 8 ? highSchoolUnits : middleSchoolUnits).map((unit) => {
    const unitCompletedStandards = completedLessons
      .filter((lesson) => lesson.unitId === unit.unit)
      .map((unit) => unit.standards)
      .flat();
    const uniqueCompleteStandards = [...new Set(unitCompletedStandards)];
    const unitIncompleteStandards = incompleteLessons
      .filter((lesson) => lesson.unitId === unit.unit)
      .map((unit) => unit.standards)
      .flat();
    const uniqueIncompleteStandards = [...new Set(unitIncompleteStandards)].filter(
      (standard) => !uniqueCompleteStandards.includes(standard),
    );
    return {
      id: unit.unit,
      displayName: unit.displayName,
      completeStandards: uniqueCompleteStandards,
      incompleteStandards: uniqueIncompleteStandards,
    };
  });
  return unitStandardsCompletion;
};
